import { useEffect, useRef, useState } from "react";
import { app, LiveShareHost } from "@microsoft/teams-js";
import { LiveShareClient, LivePresence, LivePresenceUser, PresenceState, TestLiveShareHost } from "@microsoft/live-share";
import { SharedMap, IFluidContainer } from "fluid-framework";

type Pt = { x: number; y: number };
type Stroke = { id: string; userId: string; color: string; points: Pt[] };
type UserData = {
  name: string;
  color: string;
  cursor: Pt | null;
  drawing: boolean;
};

const schema = {
  initialObjects: {
    presence: LivePresence<UserData>,
    strokes: SharedMap
  }
};

// Stable color per userId — hashed to HSL
function colorFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360}, 70%, 50%)`;
}

const initialsOf = (name: string) =>
  name.split(/\s+/).map((p) => p[0]?.toUpperCase() ?? "").slice(0, 2).join("") || "?";

export function Whiteboard({ inTeams }: { inTeams: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState("connecting…");
  const [users, setUsers] = useState<LivePresenceUser<UserData>[]>([]);
  const stateRef = useRef<{
    container?: IFluidContainer;
    presence?: LivePresence<UserData>;
    strokes?: SharedMap;
    me?: { id: string; name: string; color: string };
    drawing?: Stroke | null;
    lastBroadcast?: number;
  }>({});

  // ---- Setup Live Share ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let host: any;
        let displayName = "Guest";
        let userId = "anon-" + Math.random().toString(36).slice(2, 8);

        if (inTeams) {
          host = LiveShareHost.create();
          const ctx = await app.getContext();
          displayName = ctx.user?.displayName || ctx.user?.userPrincipalName || displayName;
          userId = ctx.user?.id || userId;
        } else {
          host = TestLiveShareHost.create();
          displayName = "Local user " + Math.floor(Math.random() * 100);
        }

        const me = { id: userId, name: displayName, color: colorFor(userId) };
        stateRef.current.me = me;

        const liveShare = new LiveShareClient(host);
        const { container } = await liveShare.joinContainer(schema);
        if (cancelled) return;

        const presence = container.initialObjects.presence as LivePresence<UserData>;
        const strokesMap = container.initialObjects.strokes as SharedMap;
        stateRef.current.container = container as any;
        stateRef.current.presence = presence;
        stateRef.current.strokes = strokesMap;

        presence.on("presenceChanged", () => {
          setUsers(presence.getUsers(PresenceState.online));
          drawAll();
        });
        strokesMap.on("valueChanged", drawAll);

        await presence.initialize({
          name: displayName,
          color: me.color,
          cursor: null,
          drawing: false
        });

        setStatus(`connected as ${displayName}`);
        drawAll();
      } catch (e) {
        setStatus("error: " + (e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inTeams]);

  // ---- Resize canvas to its container ----
  useEffect(() => {
    const c = canvasRef.current!;
    const ro = new ResizeObserver(() => {
      const r = c.getBoundingClientRect();
      c.width = r.width * devicePixelRatio;
      c.height = r.height * devicePixelRatio;
      drawAll();
    });
    ro.observe(c);
    return () => ro.disconnect();
  }, []);

  // ---- Drawing ----
  function getCanvasPt(e: React.PointerEvent): Pt {
    const c = canvasRef.current!;
    const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  }
  function broadcastCursor(p: Pt | null, drawing: boolean) {
    const now = performance.now();
    if (p && stateRef.current.lastBroadcast && now - stateRef.current.lastBroadcast < 33) return;
    stateRef.current.lastBroadcast = now;
    const me = stateRef.current.me!;
    stateRef.current.presence?.update({
      name: me.name,
      color: me.color,
      cursor: p,
      drawing
    });
  }

  function onPointerDown(e: React.PointerEvent) {
    (e.target as Element).setPointerCapture(e.pointerId);
    const me = stateRef.current.me!;
    const stroke: Stroke = {
      id: `${me.id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      userId: me.id,
      color: me.color,
      points: [getCanvasPt(e)]
    };
    stateRef.current.drawing = stroke;
    stateRef.current.strokes?.set(stroke.id, stroke);
    broadcastCursor(stroke.points[0], true);
  }
  function onPointerMove(e: React.PointerEvent) {
    const cur = stateRef.current.drawing;
    const p = getCanvasPt(e);
    if (cur) {
      cur.points.push(p);
      stateRef.current.strokes?.set(cur.id, { ...cur });
      drawAll();
    }
    broadcastCursor(p, !!cur);
  }
  function onPointerUp() {
    if (stateRef.current.drawing) {
      stateRef.current.drawing = null;
      broadcastCursor(null, false);
    }
  }

  function clearAll() {
    const m = stateRef.current.strokes;
    if (!m) return;
    for (const k of Array.from(m.keys())) m.delete(k);
  }

  // ---- Render canvas + cursor overlay ----
  function drawAll() {
    const c = canvasRef.current;
    const m = stateRef.current.strokes;
    if (!c || !m) return;
    const ctx = c.getContext("2d")!;
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 3 * devicePixelRatio;
    for (const v of Array.from(m.values()) as Stroke[]) {
      if (!v?.points?.length) continue;
      ctx.strokeStyle = v.color;
      ctx.beginPath();
      ctx.moveTo(v.points[0].x * c.width, v.points[0].y * c.height);
      for (let i = 1; i < v.points.length; i++) {
        ctx.lineTo(v.points[i].x * c.width, v.points[i].y * c.height);
      }
      ctx.stroke();
    }
  }

  const myId = stateRef.current.me?.id;
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: "#fafafa" }}>
      <div style={{ padding: "8px 12px", display: "flex", alignItems: "center", gap: 12, borderBottom: "1px solid #e5e5e5", background: "white" }}>
        <strong>🎨 Live Whiteboard</strong>
        <span style={{ fontSize: 12, opacity: 0.6 }}>{status}</span>
        <div style={{ flex: 1 }} />
        <UserList users={users} />
        <button onClick={clearAll} style={{ padding: "4px 10px", border: "1px solid #ddd", background: "white", borderRadius: 4, cursor: "pointer" }}>Clear</button>
      </div>
      <div ref={overlayRef} style={{ position: "relative", flex: 1, overflow: "hidden" }}>
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onPointerLeave={(e) => { if (stateRef.current.drawing) onPointerUp(); broadcastCursor(null, false); }}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", touchAction: "none", cursor: "crosshair", background: "white" }}
        />
        {users.map((u) => {
          const d = u.data;
          if (!d?.cursor || u.userId === myId) return null;
          return (
            <Cursor key={u.userId} x={d.cursor.x} y={d.cursor.y} color={d.color} name={d.name} />
          );
        })}
      </div>
    </div>
  );
}

function Cursor({ x, y, color, name }: { x: number; y: number; color: string; name: string }) {
  return (
    <div
      style={{
        position: "absolute",
        left: `${x * 100}%`,
        top: `${y * 100}%`,
        transform: "translate(-2px, -2px)",
        pointerEvents: "none",
        transition: "left 60ms linear, top 60ms linear",
        zIndex: 10
      }}
    >
      <svg width="20" height="22" viewBox="0 0 20 22" style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))" }}>
        <path d="M2 2 L2 18 L7 14 L10 20 L13 19 L10 13 L17 13 Z" fill={color} stroke="white" strokeWidth="1.2" />
      </svg>
      <div style={{
        position: "absolute", left: 18, top: 16, display: "flex", alignItems: "center", gap: 6,
        background: color, color: "white", padding: "2px 8px 2px 2px", borderRadius: 999,
        fontSize: 12, fontFamily: "Segoe UI, sans-serif", whiteSpace: "nowrap", boxShadow: "0 1px 3px rgba(0,0,0,0.25)"
      }}>
        <Avatar name={name} bg="rgba(255,255,255,0.25)" size={18} />
        {name}
      </div>
    </div>
  );
}

function Avatar({ name, bg, size = 24 }: { name: string; bg: string; size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", background: bg, color: "white",
      display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.45,
      fontWeight: 600, flex: "0 0 auto"
    }}>
      {initialsOf(name)}
    </div>
  );
}

function UserList({ users }: { users: LivePresenceUser<UserData>[] }) {
  return (
    <div style={{ display: "flex", gap: -4 }}>
      {users.map((u, i) => (
        <div key={u.userId} title={u.data?.name} style={{ marginLeft: i === 0 ? 0 : -6, border: "2px solid white", borderRadius: "50%" }}>
          <Avatar name={u.data?.name || "?"} bg={u.data?.color || "#888"} />
        </div>
      ))}
    </div>
  );
}
